import {useState, useEffect, type ReactNode} from 'react'
import {Switch} from '@/components/ui/switch'
import {DetailField} from '@/components/shared/DetailField'
import {Stack} from '@/components/shared/Stack'
import {InlineRow} from '@/components/shared/InlineRow'
import {Label} from '@/components/ui/label'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {ErrorText} from '@/components/shared/ErrorText'

interface InlineCheckboxFieldProps {
    label: ReactNode
    value: boolean
    canEdit: boolean
    onCommit: (newValue: boolean) => Promise<void>
    enabledText: string
    disabledText: string
    fullWidth?: boolean
    testId?: string
    id?: string
    'aria-label'?: string
}

export function InlineCheckboxField({
    label,
    value,
    canEdit,
    onCommit,
    enabledText,
    disabledText,
    fullWidth,
    testId,
    id,
    'aria-label': ariaLabel,
}: InlineCheckboxFieldProps) {
    const [localValue, setLocalValue] = useState(value)
    const [error, setError] = useState<string | null>(null)
    const [pending, setPending] = useState(false)

    useEffect(() => {
        setLocalValue(value)
    }, [value])

    if (!canEdit) {
        return (
            <DetailField
                label={label}
                fullWidth={fullWidth}
                value={
                    <Badge variant={value ? BadgeVariant.SUCCESS : 'outline'}>
                        {value ? enabledText : disabledText}
                    </Badge>
                }
            />
        )
    }

    const handleChange = async (next: boolean) => {
        setLocalValue(next)
        setError(null)
        setPending(true)
        try {
            await onCommit(next)
        } catch (err) {
            setLocalValue(value)
            const msg = err instanceof Error ? err.message : String(err)
            setError(msg)
        } finally {
            setPending(false)
        }
    }

    return (
        <DetailField
            label={label}
            fullWidth={fullWidth}
            value={
                <Stack gap="xs">
                    <InlineRow gap="sm" align="center">
                        <Switch
                            id={id}
                            checked={localValue}
                            disabled={pending}
                            data-testid={testId}
                            aria-label={ariaLabel}
                            onCheckedChange={handleChange}
                        />
                        <Label htmlFor={id}>
                            {localValue ? enabledText : disabledText}
                        </Label>
                    </InlineRow>
                    {error && (
                        <ErrorText data-testid={testId ? `${testId}-error` : undefined}>
                            {error}
                        </ErrorText>
                    )}
                </Stack>
            }
        />
    )
}
